import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { youtubeEmbedUrl } from "@/lib/youtube";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft,
  ImagePlus,
  Type,
  Heading,
  Quote,
  Trash2,
  ArrowUp,
  ArrowDown,
  Loader2,
  Bold,
  X,
  Youtube,
  Map,
  Search,
} from "lucide-react";

const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `b_${Date.now()}_${Math.random().toString(36).slice(2)}`);

const emptyBlock = (type) => ({
  _id: uid(),
  type,
  text: "",
  url: "",
  caption: "",
});

const uploadFile = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.url; // /api/files/...
};

// Auto-growing textarea: expands vertically to fit content so text is never cramped.
// `resizable` adds a drag handle so the author can enlarge it further at will.
function GrowTextarea({ value, onChange, minHeight = 120, resizable = false, className = "", textareaRef, ...props }) {
  const ref = useRef(null);
  const attach = (el) => {
    ref.current = el;
    if (textareaRef) textareaRef(el);
  };
  const grow = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
  };
  useEffect(() => {
    grow();
  }, [value, minHeight]);
  return (
    <Textarea
      ref={attach}
      value={value}
      onChange={onChange}
      onInput={grow}
      style={{ minHeight, overflow: "hidden" }}
      className={`${resizable ? "resize-y" : "resize-none"} ${className}`}
      {...props}
    />
  );
}

const escapeHtml = (s) =>
  (s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escapeRegExp = (s) => (s || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Wrap every case-insensitive occurrence of `term` in <mark>, HTML-escaping the rest.
const buildHighlightHtml = (text, term) => {
  const src = text || "";
  const t = (term || "").trim();
  if (!t) return escapeHtml(src);
  const re = new RegExp(escapeRegExp(t), "gi");
  let out = "";
  let last = 0;
  let m;
  while ((m = re.exec(src)) !== null) {
    out += escapeHtml(src.slice(last, m.index));
    out += "<mark>" + escapeHtml(m[0]) + "</mark>";
    last = m.index + m[0].length;
    if (m.index === re.lastIndex) re.lastIndex++;
  }
  out += escapeHtml(src.slice(last));
  return out;
};

const countMatches = (text, term) => {
  const t = (term || "").trim();
  if (!t) return 0;
  const re = new RegExp(escapeRegExp(t), "gi");
  return ((text || "").match(re) || []).length;
};

// Multiline field with a highlight backdrop. Visible text comes from the
// <textarea> on top; the backdrop behind paints the yellow <mark> for matches.
// Both share the exact same typography + padding so highlights line up.
function HighlightTextarea({ value, onChange, highlight, minHeight = 120, resizable = false, typoClass = "", padClass = "", wrapperClass = "", textareaRef, ...props }) {
  const ref = useRef(null);
  const attach = (el) => {
    ref.current = el;
    if (textareaRef) textareaRef(el);
  };
  const grow = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
  };
  useEffect(() => {
    grow();
  }, [value, minHeight]);
  const box = `${typoClass} ${padClass} w-full block bg-transparent border-0 m-0`;
  return (
    <div className={`relative ${wrapperClass}`}>
      <div
        aria-hidden="true"
        className={`hl-backdrop absolute inset-0 overflow-hidden whitespace-pre-wrap break-words pointer-events-none ${box}`}
        dangerouslySetInnerHTML={{ __html: buildHighlightHtml(value, highlight) + "\n" }}
      />
      <textarea
        ref={attach}
        value={value}
        onChange={onChange}
        onInput={grow}
        style={{ minHeight, overflow: "hidden" }}
        className={`relative z-[1] focus:outline-none focus-visible:outline-none placeholder:text-[#A8A29E] ${resizable ? "resize-y" : "resize-none"} ${box}`}
        {...props}
      />
    </div>
  );
}

// Single-line field with a highlight backdrop (used for section headings).
function HighlightInput({ value, onChange, highlight, typoClass = "", padClass = "", ...props }) {
  const box = `${typoClass} ${padClass} w-full block bg-transparent border-0 m-0`;
  return (
    <div className="relative">
      <div
        aria-hidden="true"
        className={`hl-backdrop absolute inset-0 overflow-hidden whitespace-pre pointer-events-none flex items-center ${box}`}
      >
        <span dangerouslySetInnerHTML={{ __html: buildHighlightHtml(value, highlight) }} />
      </div>
      <input
        value={value}
        onChange={onChange}
        className={`relative z-[1] focus:outline-none focus-visible:outline-none placeholder:text-[#A8A29E] ${box}`}
        {...props}
      />
    </div>
  );
}

const WIDTHS = {
  media: "max-w-4xl",
  ampia: "max-w-6xl",
  piena: "max-w-[100rem]",
};

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);

  const [title, setTitle] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [cover, setCover] = useState("");
  const [tags, setTags] = useState("");
  const [category, setCategory] = useState("");
  const [catOptions, setCatOptions] = useState([]);

  useEffect(() => {
    api.get("/categories").then((res) => setCatOptions(res.data.map((c) => c.name))).catch(() => {});
  }, []);
  const [status, setStatus] = useState("published");
  const [blocks, setBlocks] = useState([emptyBlock("paragraph")]);
  const [coverUploading, setCoverUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const coverInput = useRef(null);
  const [width, setWidth] = useState("ampia");
  const widthClass = WIDTHS[width];
  const blockRefs = useRef({});
  const [find, setFind] = useState("");
  const findTerm = find.trim();
  const totalMatches = findTerm
    ? blocks.reduce(
        (n, b) =>
          ["paragraph", "heading", "quote"].includes(b.type)
            ? n + countMatches(b.text, findTerm)
            : n,
        0
      )
    : 0;

  const applyBold = (i, id) => {
    const el = blockRefs.current[id];
    const text = blocks[i].text || "";
    if (!el || el.selectionStart === el.selectionEnd) {
      toast.error("Seleziona prima il testo da mettere in grassetto");
      return;
    }
    const s = el.selectionStart;
    const e = el.selectionEnd;
    const newText = text.slice(0, s) + "**" + text.slice(s, e) + "**" + text.slice(e);
    updateBlock(i, { text: newText });
  };

  useEffect(() => {
    if (!editing) return;
    api
      .get("/admin/posts")
      .then((res) => {
        const meta = res.data.find((p) => p.id === id);
        if (meta) {
          return api.get(`/posts/${meta.slug}`);
        }
        throw new Error("not found");
      })
      .then((res) => {
        const p = res.data;
        setTitle(p.title || "");
        setExcerpt(p.excerpt || "");
        setCover(p.cover_image || "");
        setTags((p.tags || []).join(", "));
        setCategory(p.category || "");
        setStatus(p.status || "published");
        setBlocks(
          p.content?.length
            ? p.content.map((b) => ({ ...b, _id: uid() }))
            : [emptyBlock("paragraph")]
        );
      })
      .catch(() => toast.error("Articolo non trovato"));
  }, [id, editing]);

  const handleCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const url = await uploadFile(file);
      setCover(url);
      toast.success("Copertina caricata");
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setCoverUploading(false);
    }
  };

  const updateBlock = (i, patch) =>
    setBlocks((bs) => bs.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));

  const addBlock = (type) => setBlocks((bs) => [...bs, emptyBlock(type)]);
  const removeBlock = (i) => setBlocks((bs) => bs.filter((_, idx) => idx !== i));
  const moveBlock = (i, dir) =>
    setBlocks((bs) => {
      const j = i + dir;
      if (j < 0 || j >= bs.length) return bs;
      const copy = [...bs];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });

  const handleBlockImage = async (i, file) => {
    if (!file) return;
    updateBlock(i, { uploading: true });
    try {
      const url = await uploadFile(file);
      updateBlock(i, { url, uploading: false });
    } catch {
      updateBlock(i, { uploading: false });
      toast.error("Errore nel caricamento immagine");
    }
  };

  const save = async () => {
    if (!title.trim()) {
      toast.error("Aggiungi un titolo");
      return;
    }
    setSaving(true);
    const payload = {
      title: title.trim(),
      excerpt: excerpt.trim(),
      cover_image: cover || null,
      tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      category: category.trim(),
      status,
      content: blocks
        .filter((b) => (b.type === "image" || b.type === "video" ? b.url : b.text?.trim()))
        .map(({ type, text, url, caption }) => ({ type, text, url, caption })),
    };
    try {
      if (editing) {
        await api.put(`/posts/${id}`, payload);
      } else {
        await api.post("/posts", payload);
      }
      toast.success(status === "published" ? "Articolo pubblicato" : "Bozza salvata");
      navigate("/admin");
    } catch {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative z-10 min-h-screen bg-[#F9F8F6]">
      {/* Top bar */}
      <div className="border-b border-[#E7E5E4] bg-[#F2F0EB] sticky top-0 z-30">
        <div className={`${widthClass} mx-auto px-6 py-5 flex items-center justify-between gap-4`}>
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 label-ui text-[#57534E] hover:text-[#9F583F] transition-colors"
          >
            <ArrowLeft size={15} strokeWidth={1.5} /> Torna
          </Link>

          <div className="hidden md:flex items-center border border-[#E7E5E4]" data-testid="width-control">
            <span className="label-ui text-[#A8A29E] px-3">Larghezza</span>
            {[
              { key: "media", label: "Media" },
              { key: "ampia", label: "Ampia" },
              { key: "piena", label: "Piena" },
            ].map((w) => (
              <button
                key={w.key}
                onClick={() => setWidth(w.key)}
                data-testid={`width-${w.key}`}
                className={`label-ui px-3 py-2 transition-colors ${
                  width === w.key ? "bg-[#1C1917] text-white" : "text-[#57534E] hover:text-[#9F583F]"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <div className="flex border border-[#E7E5E4]">
              <button
                onClick={() => setStatus("draft")}
                data-testid="status-draft"
                className={`label-ui px-4 py-2 transition-colors ${
                  status === "draft" ? "bg-[#1C1917] text-white" : "text-[#57534E]"
                }`}
              >
                Bozza
              </button>
              <button
                onClick={() => setStatus("published")}
                data-testid="status-published"
                className={`label-ui px-4 py-2 transition-colors ${
                  status === "published" ? "bg-[#9F583F] text-white" : "text-[#57534E]"
                }`}
              >
                Pubblica
              </button>
            </div>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="save-post-button"
              className="rounded-none bg-[#1C1917] hover:bg-[#9F583F] text-white label-ui px-6 py-5 h-auto transition-colors"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : "Salva"}
            </Button>
          </div>
        </div>

        {/* Find in text — highlights matches without changing the article */}
        <div className="border-t border-[#E7E5E4]">
          <div className={`${widthClass} mx-auto px-6 py-3 flex items-center gap-3`}>
            <Search size={16} strokeWidth={1.6} className="text-[#A8A29E] shrink-0" />
            <input
              type="text"
              value={find}
              onChange={(e) => setFind(e.target.value)}
              placeholder="Cerca una parola nel testo…"
              data-testid="editor-find-input"
              className="flex-1 max-w-md font-ui text-sm bg-transparent border-0 focus:outline-none placeholder:text-[#A8A29E] text-[#1C1917]"
            />
            {findTerm && (
              <span
                data-testid="editor-find-count"
                className={`label-ui shrink-0 ${totalMatches > 0 ? "text-[#9F583F]" : "text-[#A8A29E]"}`}
              >
                {totalMatches} {totalMatches === 1 ? "occorrenza" : "occorrenze"}
              </span>
            )}
            {find && (
              <button
                onClick={() => setFind("")}
                data-testid="editor-find-clear"
                aria-label="Cancella ricerca"
                className="text-[#A8A29E] hover:text-[#9F583F] shrink-0"
              >
                <X size={15} />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={`${widthClass} mx-auto px-6 py-12`}>
        {/* Cover */}
        <div className="mb-10">
          <label className="label-ui text-[#57534E] block mb-3">Immagine di copertina</label>
          {cover ? (
            <div className="relative group">
              <img
                src={mediaUrl(cover)}
                alt="Copertina"
                className="w-full aspect-[21/9] object-cover"
                data-testid="cover-preview"
              />
              <button
                onClick={() => setCover("")}
                className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-white/90 hover:bg-white text-[#1C1917] transition-colors"
                data-testid="remove-cover-button"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => coverInput.current?.click()}
              data-testid="upload-cover-button"
              className="w-full aspect-[21/9] border border-dashed border-[#E7E5E4] flex flex-col items-center justify-center gap-3 text-[#57534E] hover:border-[#9F583F] hover:text-[#9F583F] transition-colors"
            >
              {coverUploading ? (
                <Loader2 size={26} className="animate-spin" />
              ) : (
                <>
                  <ImagePlus size={26} strokeWidth={1.5} />
                  <span className="label-ui">Carica copertina</span>
                </>
              )}
            </button>
          )}
          <input
            ref={coverInput}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleCover}
          />
        </div>

        {/* Title */}
        <GrowTextarea
          data-testid="post-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Titolo dell'articolo"
          minHeight={70}
          className="font-display text-4xl md:text-5xl tracking-tight text-[#1C1917] border-0 bg-transparent px-0 focus-visible:ring-0 leading-tight placeholder:text-[#A8A29E] mb-6"
        />

        {/* Excerpt */}
        <GrowTextarea
          data-testid="post-excerpt-input"
          value={excerpt}
          onChange={(e) => setExcerpt(e.target.value)}
          placeholder="Sottotitolo o breve introduzione…"
          minHeight={60}
          className="font-display italic text-xl text-[#57534E] border-0 bg-transparent px-0 focus-visible:ring-0 placeholder:text-[#A8A29E] mb-6"
        />

        {/* Tags */}
        <Input
          data-testid="post-tags-input"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="Tag separati da virgola (es. ragionamenti, curiosità)"
          className="font-ui text-sm rounded-none border-x-0 border-t-0 border-b border-[#E7E5E4] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#9F583F] mb-6"
        />

        {/* Category (Giraffe Map) */}
        <div className="mb-10">
          <label className="label-ui text-[#57534E] flex items-center gap-2 mb-2">
            <Map size={13} strokeWidth={1.5} className="text-[#9F583F]" /> Categoria per la Giraffe Map
          </label>
          <select
            data-testid="post-category-input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full font-ui text-sm rounded-none border-x-0 border-t-0 border-b border-[#E7E5E4] bg-transparent px-0 py-2 focus:outline-none focus:border-[#9F583F] text-[#1C1917]"
          >
            <option value="">— Nessuna categoria —</option>
            {catOptions.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
            {category && !catOptions.includes(category) && (
              <option value={category}>{category} (rimossa)</option>
            )}
          </select>
          <p className="label-ui text-[#A8A29E] mt-2">
            L'articolo finirà automaticamente in questa isola della Giraffe Map.
          </p>
        </div>

        <div className="border-t border-[#E7E5E4] pt-8 space-y-4" data-testid="blocks-editor">
          {blocks.map((block, i) => (
            <div key={block._id} className="group relative border border-transparent hover:border-[#E7E5E4] p-3 -mx-3 transition-colors">
              {/* controls */}
              <div className="absolute -left-1 top-1/2 -translate-y-1/2 -translate-x-full pr-2 flex-col gap-1 hidden group-hover:flex">
                {(block.type === "paragraph" || block.type === "quote") && (
                  <button onClick={() => applyBold(i, block._id)} data-testid={`bold-block-${i}`} title="Grassetto (seleziona il testo)" className="text-[#A8A29E] hover:text-[#9F583F]"><Bold size={15} /></button>
                )}
                <button onClick={() => moveBlock(i, -1)} className="text-[#A8A29E] hover:text-[#1C1917]"><ArrowUp size={15} /></button>
                <button onClick={() => moveBlock(i, 1)} className="text-[#A8A29E] hover:text-[#1C1917]"><ArrowDown size={15} /></button>
                <button onClick={() => removeBlock(i)} data-testid={`remove-block-${i}`} className="text-[#A8A29E] hover:text-red-600"><Trash2 size={15} /></button>
              </div>

              {block.type === "paragraph" && (
                <HighlightTextarea
                  value={block.text}
                  onChange={(e) => updateBlock(i, { text: e.target.value })}
                  highlight={find}
                  textareaRef={(el) => { blockRefs.current[block._id] = el; }}
                  placeholder="Scrivi un paragrafo… (vai a capo per creare nuove righe; seleziona una parola e premi B per il grassetto)"
                  minHeight={200}
                  resizable
                  data-testid={`block-paragraph-${i}`}
                  typoClass="font-body text-xl text-[#292524] leading-loose"
                  padClass="px-0 py-2"
                />
              )}
              {block.type === "heading" && (
                <HighlightInput
                  value={block.text}
                  onChange={(e) => updateBlock(i, { text: e.target.value })}
                  highlight={find}
                  placeholder="Titolo di sezione"
                  data-testid={`block-heading-${i}`}
                  typoClass="font-display text-3xl tracking-tight text-[#1C1917] leading-normal"
                  padClass="px-0 py-2"
                />
              )}
              {block.type === "quote" && (
                <HighlightTextarea
                  value={block.text}
                  onChange={(e) => updateBlock(i, { text: e.target.value })}
                  highlight={find}
                  textareaRef={(el) => { blockRefs.current[block._id] = el; }}
                  placeholder="Una citazione…"
                  minHeight={90}
                  data-testid={`block-quote-${i}`}
                  typoClass="font-display italic text-2xl text-[#1C1917]"
                  padClass="pl-6 pr-3 py-2"
                  wrapperClass="border-l-2 border-[#9F583F]"
                />
              )}
              {block.type === "image" && (
                <div>
                  {block.url ? (
                    <div className="relative group/img">
                      <img src={mediaUrl(block.url)} alt="" className="w-full object-cover" />
                      <button
                        onClick={() => updateBlock(i, { url: "" })}
                        title="Rimuovi / sostituisci immagine"
                        data-testid={`remove-block-image-${i}`}
                        className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center bg-white/90 hover:bg-white text-[#1C1917] transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="w-full aspect-[16/9] border border-dashed border-[#E7E5E4] flex flex-col items-center justify-center gap-2 text-[#57534E] hover:border-[#9F583F] hover:text-[#9F583F] transition-colors cursor-pointer" data-testid={`block-image-upload-${i}`}>
                      {block.uploading ? (
                        <Loader2 size={22} className="animate-spin" />
                      ) : (
                        <>
                          <ImagePlus size={22} strokeWidth={1.5} />
                          <span className="label-ui">Carica immagine</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleBlockImage(i, e.target.files?.[0])}
                      />
                    </label>
                  )}
                  <Input
                    value={block.caption}
                    onChange={(e) => updateBlock(i, { caption: e.target.value })}
                    placeholder="Didascalia (opzionale)"
                    className="font-ui text-xs uppercase tracking-widest rounded-none border-0 bg-transparent px-0 focus-visible:ring-0 text-center text-[#57534E] mt-3 placeholder:text-[#A8A29E]"
                  />
                </div>
              )}
              {block.type === "video" && (
                <div>
                  <Input
                    value={block.url}
                    onChange={(e) => updateBlock(i, { url: e.target.value })}
                    placeholder="Incolla il link del video YouTube (es. https://www.youtube.com/watch?v=...)"
                    data-testid={`block-video-url-${i}`}
                    className="font-ui text-sm rounded-none border-x-0 border-t-0 border-b border-[#E7E5E4] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#9F583F] mb-4"
                  />
                  {youtubeEmbedUrl(block.url) ? (
                    <div className="relative w-full aspect-video bg-black">
                      <iframe
                        src={youtubeEmbedUrl(block.url)}
                        title="Anteprima video YouTube"
                        className="absolute inset-0 w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        data-testid={`block-video-preview-${i}`}
                      />
                    </div>
                  ) : (
                    <div className="w-full aspect-video border border-dashed border-[#E7E5E4] flex flex-col items-center justify-center gap-2 text-[#57534E]">
                      <Youtube size={26} strokeWidth={1.5} />
                      <span className="label-ui">
                        {block.url ? "Link YouTube non valido" : "Incolla un link YouTube qui sopra"}
                      </span>
                    </div>
                  )}
                  <Input
                    value={block.caption}
                    onChange={(e) => updateBlock(i, { caption: e.target.value })}
                    placeholder="Didascalia (opzionale)"
                    className="font-ui text-xs uppercase tracking-widest rounded-none border-0 bg-transparent px-0 focus-visible:ring-0 text-center text-[#57534E] mt-3 placeholder:text-[#A8A29E]"
                  />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add block toolbar */}
        <div className="flex flex-wrap gap-3 mt-8 pt-8 border-t border-[#E7E5E4]">
          {[
            { type: "paragraph", label: "Paragrafo", icon: Type },
            { type: "heading", label: "Titolo", icon: Heading },
            { type: "quote", label: "Citazione", icon: Quote },
            { type: "image", label: "Immagine", icon: ImagePlus },
            { type: "video", label: "Video YouTube", icon: Youtube },
          ].map((b) => (
            <button
              key={b.type}
              onClick={() => addBlock(b.type)}
              data-testid={`add-block-${b.type}`}
              className="inline-flex items-center gap-2 label-ui px-4 py-3 border border-[#E7E5E4] text-[#57534E] hover:border-[#9F583F] hover:text-[#9F583F] transition-colors"
            >
              <b.icon size={15} strokeWidth={1.5} /> {b.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
